/**
 * Kalkylfil CSV parser (DEV-3298) — `csvText` → `KalkylfilParseResult`.
 *
 * Seven-step pipeline: detect layout → catalog + denormalize wide rows → tag
 * priority → column mapping → freeze plan → build rows → attach cell metadata.
 * See `client/features/csv-import/refactor-csv-importer.md`.
 */
import {
  isNumeric,
  removeNumberFormatting,
} from '@shared/helpers/math.helpers';
import {
  IFC_MEASURE_HEADER_KEYS,
  IFC_TYPE_HEADER_KEYS,
  deriveIfcQuantity,
  type IfcMeasure,
} from '@shared/helpers/ifc-quantity.helpers';
import { isSbefCode } from '@shared/helpers/sbef.helpers';
import {
  convertSbef,
  detectBsabStandard,
  isIgnoredSbefCell,
  type BsabStandard,
} from '@shared/helpers/sbef-standard.helpers';
import {
  detectWasteScale,
  WasteScale,
} from '@shared/helpers/waste-scale.helpers';
import Papa from 'papaparse';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

/** Canonical Nodon field a CSV column can map to. */
export type NodonField =
  'sbef' | 'code' | 'name' | 'quantity' | 'unit' | 'waste';

/** Map from canonical Nodon field to source-CSV header name. */
export type KalkylfilColumnMap = Partial<Record<NodonField, string>>;

/**
 * Human-readable label for each canonical field. Used by UI surfaces (e.g.
 * the metadata strip) to display "Quantity (Mängd)" alongside the original
 * CSV header so users see both the normalised role and the source name.
 */
export const NODON_FIELD_LABEL: Record<NodonField, string> = {
  sbef: 'SBEF',
  code: 'Code',
  name: 'Name',
  quantity: 'Quantity',
  unit: 'Unit',
  waste: 'Waste',
};

export type KalkylfilRow = {
  sbef: string;
  code: string;
  name: string;
  quantity: number | null;
  unit: string;
  waste: number | null;
  /** Flattened row keyed by `Tag.Header` column keys — every value final. */
  raw: Record<string, string>;
};

export type KalkylfilParseError =
  | { kind: 'header_not_found' }
  | { kind: 'missing_required_fields'; fields: NodonField[] }
  | { kind: 'row_error'; row: number; reason: string };

// ---------------------------------------------------------------------------
// Synonym table — drives header detection and auto-mapping.
// Keep entries lowercase, accent-stripped, trimmed. New synonyms safe to add.
// ---------------------------------------------------------------------------

const FIELD_SYNONYMS: Record<NodonField, readonly string[]> = {
  sbef: [
    'sbef',
    'byggdel',
    'huvudaktivitet',
    'aktivitet',
    'kategori',
    'category',
    'section',
    'classification',
    'kod',
    // CC LCA export (DEV-3424): the top-level classification (ElementClassCode1)
    // holds the SBEF code. Deeper levels (ElementClassCode2, …) stay unmapped.
    'elementclasscode1',
  ],
  code: [
    'code',
    'resurskod',
    'kod',
    'artnr',
    'artikelnummer',
    'artikelnr',
    'itemcode',
    // CC LCA export (DEV-3424): the resource id.
    'resourceid',
  ],
  name: [
    'name',
    'benamning',
    'text',
    'namn',
    'beskrivning',
    'description',
    'resurs',
    'resursnamn',
    // CC LCA export (DEV-3424).
    'resourcename',
  ],
  quantity: [
    'quantity',
    'mangd',
    'antal',
    'qty',
    'amount',
    'volym',
    // CC LCA export (DEV-3424).
    'amountbought',
  ],
  unit: [
    'unit',
    'enhet',
    'uom',
    // CC LCA export (DEV-3424).
    'resourceunit',
  ],
  waste: ['waste', 'spill', 'spillfaktor', 'wastefactor'],
};

/** Lowercase, strip diacritics, trim. Used for header synonym matching only. */
export const headerSynonymKey = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Case-insensitive key for matching CSV headers. {@link KalkylfilRow.raw} and
 * the persisted import headers keep literal CSV header text.
 */
export const headerKey = (s: string): string => s.toLowerCase().trim();

/**
 * Read one {@link KalkylfilRow.raw} cell by import header name. Matches
 * case-insensitively so UI order keys align with persisted raw keys.
 */
export const readKalkylfilRawCell = (
  raw: Record<string, string | number | null | undefined>,
  header: string,
): string => {
  const direct = raw[header];
  if (direct !== undefined && direct !== null) return String(direct);
  const key = headerKey(header);
  const match = Object.keys(raw).find((k) => headerKey(k) === key);
  if (match === undefined) return '';
  const v = raw[match];
  return v === undefined || v === null ? '' : String(v);
};

const csvHeaderCell = (cell: string): string => cell.trim();

/**
 * Synonym → candidate fields. A synonym may map to multiple fields when the
 * same word is reused across domains (e.g. `kod` = SBEF code or resource
 * code). When that happens {@link disambiguateField} picks the winner using
 * {@link FIELD_VALIDATORS} against sample column values.
 */
const SYNONYM_TO_FIELDS: Record<string, NodonField[]> = Object.create(null);
for (const [field, syns] of Object.entries(FIELD_SYNONYMS) as [
  NodonField,
  readonly string[],
][]) {
  for (const syn of syns) {
    const key = headerSynonymKey(syn);
    (SYNONYM_TO_FIELDS[key] ??= []).push(field);
  }
}

/**
 * Per-field value validators used to disambiguate columns whose header
 * matches multiple fields (see {@link SYNONYM_TO_FIELDS}). A validator
 * returns `true` if the value plausibly belongs to that field. Keep
 * validators strict: false positives mis-assign columns.
 */
const FIELD_VALIDATORS: Partial<
  Record<NodonField, (value: string) => boolean>
> = {
  sbef: (v) => isSbefCode(v.trim()),
  quantity: (v) => isNumeric(removeNumberFormatting(v.trim())),
  // Loose on purpose: waste/spill can be a factor, percentage, or fraction.
  waste: (v) => isNumeric(removeNumberFormatting(v.trim())),
  // Loose: anything non-numeric. Distinguishes "m3"/"kg" from numeric columns.
  unit: (v) => {
    const t = v.trim();
    return t.length > 0 && !isNumeric(removeNumberFormatting(t));
  },
};

/** Minimum fraction of samples that must pass a validator for it to win. */
const VALIDATOR_PASS_THRESHOLD = 0.5;

/**
 * Resolve a synonym match with multiple candidate fields by running each
 * candidate's validator against column sample values. Single-candidate
 * synonyms and ambiguous matches without samples fall through to the
 * first-listed candidate (legacy first-wins behaviour).
 */
const disambiguateField = (
  candidates: readonly NodonField[],
  sampleValues: readonly string[],
): NodonField => {
  const first = candidates[0];
  if (!first) throw new Error('disambiguateField requires ≥1 candidate');
  if (candidates.length === 1) return first;

  const nonEmpty = sampleValues.map((v) => v.trim()).filter(Boolean);
  if (nonEmpty.length === 0) return first;

  let bestStrict: { field: NodonField; rate: number } | null = null;
  let firstNeutral: NodonField | null = null;
  for (const field of candidates) {
    const validator = FIELD_VALIDATORS[field];
    if (!validator) {
      // Candidate without a validator = neutral; preferred over a validator
      // that actively rejected the samples.
      firstNeutral ??= field;
      continue;
    }
    const passed = nonEmpty.filter(validator).length;
    const rate = passed / nonEmpty.length;
    if (
      rate >= VALIDATOR_PASS_THRESHOLD &&
      (!bestStrict || rate > bestStrict.rate)
    ) {
      bestStrict = { field, rate };
    }
  }
  return bestStrict?.field ?? firstNeutral ?? first;
};

/**
 * Display order for canonical Nodon fields. Drives column ordering in the
 * candidate-rows table — quantity → unit → waste → sbef → name → code,
 * then any unmapped CSV columns in their original order.
 */
const FIELD_DISPLAY_ORDER: readonly NodonField[] = [
  'name',
  'quantity',
  'unit',
  'waste',
  'sbef',
  'code',
];

/** True when the body has at least one non-empty value in this column. */
const columnHasBodyData = (samples: readonly string[]): boolean =>
  samples.some((v) => v.trim() !== '');

/**
 * Whether a header cell may claim a Nodon field during detection. When body
 * samples are provided, all-empty columns are ignored entirely.
 */
const shouldClaimHeaderCell = (
  sampleColumnValues: readonly (readonly string[])[] | undefined,
  colIdx: number,
): boolean => {
  if (sampleColumnValues === undefined) return true;
  return columnHasBodyData(sampleColumnValues[colIdx] ?? []);
};

/**
 * Score a header candidate by counting cells matching any field synonym. Each
 * field counts at most once. When `sampleColumnValues` is provided, ambiguous
 * synonyms are resolved via {@link disambiguateField} and all-empty columns
 * never enter the map.
 */
const scoreHeaderRow = (
  cells: readonly string[],
  sampleColumnValues?: readonly (readonly string[])[],
): { score: number; columnMap: KalkylfilColumnMap } => {
  const columnMap: KalkylfilColumnMap = {};
  cells.forEach((cell, colIdx) => {
    if (!shouldClaimHeaderCell(sampleColumnValues, colIdx)) return;
    const candidates = SYNONYM_TO_FIELDS[headerSynonymKey(cell)];
    if (!candidates || candidates.length === 0) return;
    const samples = sampleColumnValues?.[colIdx] ?? [];
    const field = disambiguateField(candidates, samples);
    if (!(field in columnMap)) columnMap[field] = csvHeaderCell(cell);
  });
  return { score: Object.keys(columnMap).length, columnMap };
};

/**
 * Build a {@link KalkylfilColumnMap} from a bare header list by running the
 * same synonym matching used during detection. Lets consumers that only
 * persist `headers: string[]` recover the canonical mapping at render time.
 *
 * @param sampleRows optional body rows (keyed by header name) used to
 *   disambiguate ambiguous synonyms and drop all-empty column claims.
 */
export const buildColumnMapFromHeaders = (
  headers: readonly string[],
  sampleRows?: readonly Record<string, string | number | null | undefined>[],
): KalkylfilColumnMap => {
  const sampleColumnValues = sampleRows
    ? headers.map((h) =>
        sampleRows.map((r) => {
          const v = r[h];
          return v == null ? '' : String(v);
        }),
      )
    : undefined;
  return scoreHeaderRow(headers, sampleColumnValues).columnMap;
};

/**
 * Reorder source CSV headers so mapped Nodon fields come first (in the
 * canonical {@link FIELD_DISPLAY_ORDER}) and unmapped columns follow in their
 * original order. Keeps column order consistent across CSVs.
 */
export const sortColumnsByNodonPriority = (
  columns: readonly string[],
  columnMap: KalkylfilColumnMap = buildColumnMapFromHeaders(columns),
): string[] => {
  const mappedKeys = new Set(
    FIELD_DISPLAY_ORDER.map((f) => columnMap[f])
      .filter((h): h is string => typeof h === 'string')
      .map(headerKey),
  );
  const ordered = FIELD_DISPLAY_ORDER.map((f) => {
    const mapped = columnMap[f];
    if (!mapped) return undefined;
    const key = headerKey(mapped);
    return columns.find((c) => headerKey(c) === key);
  }).filter((c): c is string => typeof c === 'string');
  const rest = columns.filter((c) => !mappedKeys.has(headerKey(c)));
  return [...ordered, ...rest];
};

// ---------------------------------------------------------------------------
// Step inputs — shared
// ---------------------------------------------------------------------------

/** One non-empty CSV line, original line index preserved. */
export type ParsedLine = { lineIndex: number; cells: string[] };

// ---------------------------------------------------------------------------
// Step 1 — Detect layout
// ---------------------------------------------------------------------------

/** One row of a tagged CSV's schema block. `headers` = cols 1..n. */
export type SchemaRow = { tag: string; headers: string[] };

export type TaggedLayout = {
  kind: 'tagged';
  /** Schema block, top → bottom. Last row = leaf header. */
  schemaRows: SchemaRow[];
  /** 0-based line index of the first body line. */
  bodyStartLine: number;
  /** Col-0 tag of the last schema row — highest priority. */
  leafTag: string;
};

export type FlatLayout = {
  kind: 'flat';
  /** 0-based line index of the detected header row. */
  headerLineIndex: number;
  /** Literal header cells of that row. */
  headers: string[];
  /** 0-based line index of the first body line. */
  bodyStartLine: number;
};

export type CsvLayout = TaggedLayout | FlatLayout;

// ---------------------------------------------------------------------------
// Step 2 — Catalog + denormalized wide rows
// ---------------------------------------------------------------------------

/** One column of the flat catalog, namespaced by its owning tag. */
export type FlatColumnCatalogEntry = {
  /** `${tag}.${originalHeader}`, deduped with `_2` suffixes within a tag. */
  normalizedHeader: string;
  /** Literal header text from the schema cell. */
  originalHeader: string;
  /** Owning tag. */
  tag: string;
  /** Index of this column in a physical body line of that tag (col 0 = tag). */
  cellIndex: number;
};

export type FlatColumnCatalog = FlatColumnCatalogEntry[];

/**
 * One leaf-tag body line, denormalized: parent-tag columns already merged in.
 * Group lines never become a `WideRow` — they only update the snapshot.
 */
export type WideRow = {
  /** Origin leaf tag of the body line that emitted this row. */
  tag: string;
  /** normalizedHeader → value. Every catalog key present; missing = `''`. */
  values: Record<string, string>;
};

// ---------------------------------------------------------------------------
// Step 3 — Tag priority
// ---------------------------------------------------------------------------

/** tag → schema index. Higher = deeper = wins ties for a field. */
export type TagPriority = ReadonlyMap<string, number>;

// ---------------------------------------------------------------------------
// Step 4 — Column mapping
// ---------------------------------------------------------------------------

/** normalizedHeader → resolved Nodon field (`undefined` = inert column). */
export type ColumnMapping = ReadonlyMap<string, NodonField | undefined>;

/** Nodon field → winning column's normalizedHeader. */
export type FieldWinner = Readonly<Partial<Record<NodonField, string>>>;

// ---------------------------------------------------------------------------
// Step 5 — Frozen plan
// ---------------------------------------------------------------------------

export type ParsePlan = {
  layout: 'tagged' | 'flat';
  catalog: FlatColumnCatalog;
  tagPriority: TagPriority;
  columnMapping: ColumnMapping;
  fieldWinner: FieldWinner;
  /** Tagged only — kept for filters / docs. */
  leafTag?: string;
};

export type FreezeResult =
  | { ok: true; plan: ParsePlan }
  | {
      ok: false;
      error: Extract<KalkylfilParseError, { kind: 'missing_required_fields' }>;
    };

// ---------------------------------------------------------------------------
// Step 7 — Cell metadata
// ---------------------------------------------------------------------------

export type ParsedCell = {
  normalizedHeader: string;
  originalHeader: string;
  tag: string;
  value: string;
  nodonField?: NodonField;
  /** True when the value was carried by denormalize, not written on this line. */
  carried?: boolean;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Minimum synonym matches for a row to count as a header. */
const MIN_HEADER_SCORE = 3;
/** How many leading lines the flat-CSV detector scans for a header. */
const HEADER_SCAN_LINES = 50;
/** Body rows sampled for validator-based disambiguation. */
const SAMPLE_ROW_COUNT = 5;
/** Implicit tag used for flat (non-tagged) CSVs. */
const FLAT_TAG = '_';
/** Fields that must have a winning column for the parse to be usable. */
const REQUIRED_FIELDS: readonly NodonField[] = ['name', 'code', 'quantity'];

// --- IFC export (DEV-3445) -------------------------------------------------
// The IFC type / measure header keys live in `ifc-quantity.helpers` so the
// mapping side can read the same columns back off a candidate row.
/** Synthetic columns derived from the IFC measure columns. */
const IFC_QUANTITY_HEADER = 'Quantity';
const IFC_UNIT_HEADER = 'Unit';
const IFC_QUANTITY_SOURCE_HEADER = 'Quantity Source';
/**
 * Solibri-specific field columns, mapped only on the IFC path so these generic
 * header names never auto-map unrelated (non-IFC) imports.
 */
const SOLIBRI_NAME_HEADER_KEY = 'type';
const SOLIBRI_CODE_HEADER_KEY = 'identity data.type mark';
/** Solibri Keynote holds BSAB 96 (DEV-3446); aliased only on the IFC path. */
const SOLIBRI_SBEF_SOURCE_HEADER_KEY = 'identity data.keynote';
const IFC_SBEF_HEADER = 'SBEF';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Convert Excel formula-style values (`="..."`) to RFC-4180 quoted fields so
 * PapaParse handles embedded `;` and `"`. The leading `=` is a text-format
 * export artefact and is dropped.
 */
const normalizeExcelFormulas = (csvText: string): string =>
  csvText.replace(
    /="(.*?)"(?=;|$)/gm,
    (_, value: string) => `"${value.replace(/"/g, '""')}"`,
  );

const parseCsvRow = (line: string): string[] => {
  const result = Papa.parse<string[]>(line, { delimiter: ';' });
  return (result.data[0] ?? []).map((c) => c.trim());
};

/** Parse non-empty CSV lines, preserving original line indices. */
const parseNonEmptyLines = (lines: readonly string[]): ParsedLine[] => {
  const out: ParsedLine[] = [];
  lines.forEach((line, lineIndex) => {
    const cells = parseCsvRow(line);
    if (cells.length === 0 || cells.every((c) => !c)) return;
    out.push({ lineIndex, cells });
  });
  return out;
};

const parseNumeric = (raw: string): number =>
  Number(removeNumberFormatting(raw.trim()));

/** Synonym-match a header list to Nodon fields; returns the match count. */
const scoreHeaders = (headers: readonly string[]): number =>
  Object.keys(buildColumnMapFromHeaders([...headers])).length;

/**
 * Resolve a single column header to a Nodon field using its body samples for
 * validator-based disambiguation. All-empty samples → `undefined` (inert).
 */
const fieldForColumn = (
  header: string,
  samples: readonly string[],
): NodonField | undefined => {
  const map = buildColumnMapFromHeaders(
    [header],
    samples.map((value) => ({ [header]: value })),
  );
  return (Object.keys(map) as NodonField[])[0];
};

// ---------------------------------------------------------------------------
// Step 1 — detectLayout
// ---------------------------------------------------------------------------

/**
 * Walk consecutive rows from the top, collecting col-0 tags until one repeats —
 * that repetition ends the schema block. Returns a `TaggedLayout` when the run
 * has ≥ 2 rows, a schema tag reappears in the body, and the leaf header scores
 * at least {@link MIN_HEADER_SCORE}. Otherwise `null` (caller tries flat).
 */
const detectTagged = (
  parsedLines: readonly ParsedLine[],
): TaggedLayout | null => {
  const schemaRows: ParsedLine[] = [];
  const seenTags = new Set<string>();
  for (const pl of parsedLines) {
    const tag = (pl.cells[0] ?? '').trim();
    if (seenTags.has(tag)) break;
    seenTags.add(tag);
    schemaRows.push(pl);
  }
  if (schemaRows.length < 2) return null;

  const leaf = schemaRows[schemaRows.length - 1]!;
  const tags = schemaRows.map((r) => (r.cells[0] ?? '').trim());
  const leafTag = tags[tags.length - 1] ?? '';

  const bodyLines = parsedLines.slice(schemaRows.length);
  const bodyTags = new Set(bodyLines.map((pl) => (pl.cells[0] ?? '').trim()));
  if (!tags.some((tag) => bodyTags.has(tag))) return null;

  // Leaf must look like a real data header, scored against its own body rows.
  const leafHeaders = leaf.cells.slice(1);
  const leafSamples = bodyLines
    .filter((pl) => (pl.cells[0] ?? '').trim() === leafTag)
    .slice(0, SAMPLE_ROW_COUNT)
    .map((pl) => {
      const record: Record<string, string> = {};
      leafHeaders.forEach((header, i) => {
        record[header] = pl.cells[i + 1] ?? '';
      });
      return record;
    });
  const leafScore = Object.keys(
    buildColumnMapFromHeaders(leafHeaders, leafSamples),
  ).length;
  if (leafScore < MIN_HEADER_SCORE) return null;

  return {
    kind: 'tagged',
    schemaRows: schemaRows.map((r) => ({
      tag: (r.cells[0] ?? '').trim(),
      headers: r.cells.slice(1),
    })),
    bodyStartLine: bodyLines[0]?.lineIndex ?? leaf.lineIndex + 1,
    leafTag,
  };
};

/**
 * IFC-export detection (DEV-3445). A flat header row that carries an IFC type
 * column (`IFC Entity` / `IFC Type`) and at least one measure column counts as a
 * flat layout even when it scores no Nodon synonyms — its quantity/unit columns
 * are derived later (see {@link augmentWithIfcQuantities}).
 */
const detectIfcLayout = (
  lines: readonly string[],
  parsedLines: readonly ParsedLine[],
): FlatLayout | null => {
  const measureKeys = new Set(Object.values(IFC_MEASURE_HEADER_KEYS));
  const scanLimit = Math.min(lines.length, HEADER_SCAN_LINES);
  for (let i = 0; i < scanLimit; i++) {
    const cells = parseCsvRow(lines[i] ?? '');
    const keys = cells.map(headerKey);
    const hasType = keys.some((k) => IFC_TYPE_HEADER_KEYS.has(k));
    const hasMeasure = keys.some((k) => measureKeys.has(k));
    if (!hasType || !hasMeasure) continue;
    const bodyStartLine =
      parsedLines.find((pl) => pl.lineIndex > i)?.lineIndex ?? i + 1;
    return { kind: 'flat', headerLineIndex: i, headers: cells, bodyStartLine };
  }
  return null;
};

/** Step 1 — classify the CSV as a tagged schema block or a flat header. */
export const detectLayout = (csvText: string): CsvLayout | null => {
  const lines = normalizeExcelFormulas(csvText).split(/\r?\n/);
  const parsedLines = parseNonEmptyLines(lines);

  const tagged = detectTagged(parsedLines);
  if (tagged) return tagged;

  const ifc = detectIfcLayout(lines, parsedLines);
  if (ifc) return ifc;

  // Flat fallback: highest-scoring row in the leading scan window.
  let best: { index: number; cells: string[]; score: number } | null = null;
  const scanLimit = Math.min(lines.length, HEADER_SCAN_LINES);
  for (let i = 0; i < scanLimit; i++) {
    const cells = parseCsvRow(lines[i] ?? '');
    if (cells.length === 0) continue;
    const score = scoreHeaders(cells);
    if (score >= MIN_HEADER_SCORE && (!best || score > best.score)) {
      best = { index: i, cells, score };
    }
  }
  if (!best) return null;

  const bodyStartLine =
    parsedLines.find((pl) => pl.lineIndex > best!.index)?.lineIndex ??
    best.index + 1;
  return {
    kind: 'flat',
    headerLineIndex: best.index,
    headers: best.cells,
    bodyStartLine,
  };
};

// ---------------------------------------------------------------------------
// Step 2 — buildCatalogAndWideRows
// ---------------------------------------------------------------------------

/** Group catalog entries by their owning tag, preserving order. */
const catalogByTag = (
  catalog: FlatColumnCatalog,
): Map<string, FlatColumnCatalogEntry[]> => {
  const map = new Map<string, FlatColumnCatalogEntry[]>();
  for (const entry of catalog) {
    const list = map.get(entry.tag) ?? [];
    list.push(entry);
    map.set(entry.tag, list);
  }
  return map;
};

const buildCatalog = (layout: CsvLayout): FlatColumnCatalog => {
  const catalog: FlatColumnCatalog = [];
  const used = new Set<string>();
  const addColumn = (
    tag: string,
    originalHeader: string,
    cellIndex: number,
  ): void => {
    const original = originalHeader.trim();
    if (!original) return;
    let normalized = `${tag}.${original}`;
    let suffix = 2;
    while (used.has(normalized)) normalized = `${tag}.${original}_${suffix++}`;
    used.add(normalized);
    catalog.push({
      normalizedHeader: normalized,
      originalHeader: original,
      tag,
      cellIndex,
    });
  };

  if (layout.kind === 'tagged') {
    // Tagged body lines carry the tag in col 0 → column cellIndex = i + 1.
    for (const schemaRow of layout.schemaRows) {
      schemaRow.headers.forEach((header, i) => {
        addColumn(schemaRow.tag, header, i + 1);
      });
    }
  } else {
    // Flat body lines have no tag column → cellIndex = i.
    layout.headers.forEach((header, i) => {
      addColumn(FLAT_TAG, header, i);
    });
  }
  return catalog;
};

/** Step 2 — build the column catalog and denormalize body lines to wide rows. */
export const buildCatalogAndWideRows = (
  layout: CsvLayout,
  parsedLines: readonly ParsedLine[],
): { catalog: FlatColumnCatalog; wideRows: WideRow[] } => {
  const catalog = buildCatalog(layout);
  const byTag = catalogByTag(catalog);
  const bodyLines = parsedLines.filter(
    (pl) => pl.lineIndex >= layout.bodyStartLine,
  );

  const snapshot: Record<string, string> = {};
  for (const entry of catalog) snapshot[entry.normalizedHeader] = '';

  const wideRows: WideRow[] = [];

  if (layout.kind === 'flat') {
    // No tags, no inheritance — every body line is one leaf wide row.
    for (const pl of bodyLines) {
      for (const entry of byTag.get(FLAT_TAG) ?? []) {
        snapshot[entry.normalizedHeader] = (
          pl.cells[entry.cellIndex] ?? ''
        ).trim();
      }
      wideRows.push({ tag: FLAT_TAG, values: { ...snapshot } });
    }
    return { catalog, wideRows };
  }

  // Tagged: tag depth = schema index. Outer tags clear deeper snapshot keys.
  const tagDepth = new Map(layout.schemaRows.map((sr, i) => [sr.tag, i]));
  for (const pl of bodyLines) {
    const tag = (pl.cells[0] ?? '').trim();
    const depth = tagDepth.get(tag);
    if (depth === undefined) continue; // unknown tag — skip

    // Group refire: clear every deeper tag's columns before writing this line.
    if (tag !== layout.leafTag) {
      for (const [otherTag, otherDepth] of tagDepth) {
        if (otherDepth <= depth) continue;
        for (const entry of byTag.get(otherTag) ?? []) {
          snapshot[entry.normalizedHeader] = '';
        }
      }
    }
    for (const entry of byTag.get(tag) ?? []) {
      snapshot[entry.normalizedHeader] = (
        pl.cells[entry.cellIndex] ?? ''
      ).trim();
    }
    // Emit only on a leaf-tag line; group lines just update the snapshot.
    if (tag === layout.leafTag) {
      wideRows.push({ tag, values: { ...snapshot } });
    }
  }
  return { catalog, wideRows };
};

// ---------------------------------------------------------------------------
// Step 2b — IFC quantity derivation (DEV-3445)
// ---------------------------------------------------------------------------

/**
 * Allocate `Tag.Header`, then `Tag.Header_2`… so a synthetic column never
 * collides with an existing catalog key (same suffix rule as {@link buildCatalog}).
 */
const uniqueNormalizedHeader = (
  catalog: FlatColumnCatalog,
  tag: string,
  originalHeader: string,
): string => {
  const used = new Set(catalog.map((entry) => entry.normalizedHeader));
  let normalized = `${tag}.${originalHeader}`;
  let suffix = 2;
  while (used.has(normalized)) {
    normalized = `${tag}.${originalHeader}_${suffix++}`;
  }
  return normalized;
};

/**
 * Native SBEF / BSAB 83 column, if the file already has one. Keynote is never
 * a native SBEF column — it is the Solibri BSAB 96 source.
 */
const findNativeSbef83Entry = (
  catalog: FlatColumnCatalog,
  wideRows: readonly WideRow[],
): FlatColumnCatalogEntry | undefined => {
  const matches: FlatColumnCatalogEntry[] = [];
  for (const entry of catalog) {
    if (headerKey(entry.originalHeader) === SOLIBRI_SBEF_SOURCE_HEADER_KEY) {
      continue;
    }
    const samples = wideRows
      .slice(0, SAMPLE_ROW_COUNT)
      .map((wide) => wide.values[entry.normalizedHeader] ?? '');
    if (fieldForColumn(entry.originalHeader, samples) !== 'sbef') continue;
    const values = wideRows.map(
      (wide) => wide.values[entry.normalizedHeader] ?? '',
    );
    if (detectBsabStandard(values) === '83') matches.push(entry);
  }
  return (
    matches.find((entry) => headerKey(entry.originalHeader) === 'sbef') ??
    matches[0]
  );
};

const appendSyntheticSbefColumn = (
  catalog: FlatColumnCatalog,
  wideRows: readonly WideRow[],
  sourceEntry: FlatColumnCatalogEntry,
  sbefStandard: BsabStandard | undefined,
): {
  catalog: FlatColumnCatalog;
  wideRows: WideRow[];
  sbefHeader: string;
} => {
  const sbefHeader = uniqueNormalizedHeader(
    catalog,
    sourceEntry.tag,
    IFC_SBEF_HEADER,
  );
  const sbefCatalogEntry: FlatColumnCatalogEntry = {
    normalizedHeader: sbefHeader,
    originalHeader: IFC_SBEF_HEADER,
    tag: sourceEntry.tag,
    cellIndex: -1,
  };
  const convertedRows = wideRows.map((wide) => {
    const raw = wide.values[sourceEntry.normalizedHeader] ?? '';
    const converted =
      sbefStandard === undefined
        ? undefined
        : convertSbef({ code: raw, from: sbefStandard, to: '83' });
    return {
      ...wide,
      values: {
        ...wide.values,
        [sbefHeader]: converted ?? '',
      },
    };
  });
  return {
    catalog: [...catalog, sbefCatalogEntry],
    wideRows: convertedRows,
    sbefHeader,
  };
};

/**
 * On the IFC/Solibri path, map SBEF from a ready BSAB 83 column when the file
 * already has one. Otherwise emit a synthetic `SBEF` column converted from
 * `Identity Data.Keynote`. Source columns stay untouched in {@link WideRow.values}.
 */
const applySolibriSbefColumn = (
  catalog: FlatColumnCatalog,
  wideRows: readonly WideRow[],
  fieldOverrides: Partial<Record<NodonField, string>>,
): {
  catalog: FlatColumnCatalog;
  wideRows: WideRow[];
  fieldOverrides: Partial<Record<NodonField, string>>;
  sbefStandard?: BsabStandard;
} => {
  const native83 = findNativeSbef83Entry(catalog, wideRows);
  if (native83) {
    return {
      catalog,
      wideRows: [...wideRows],
      fieldOverrides: { ...fieldOverrides, sbef: native83.normalizedHeader },
      sbefStandard: '83',
    };
  }

  const keynoteEntry = catalog.find(
    (e) => headerKey(e.originalHeader) === SOLIBRI_SBEF_SOURCE_HEADER_KEY,
  );
  if (!keynoteEntry) {
    return { catalog, wideRows: [...wideRows], fieldOverrides };
  }

  const sourceValues = wideRows.map(
    (wide) => wide.values[keynoteEntry.normalizedHeader] ?? '',
  );
  const sbefStandard = detectBsabStandard(sourceValues);
  // Empty / `0` / unrecognised Keynote must not force an empty synthetic
  // winner over a native SBEF column that mapColumns already found.
  if (sbefStandard === undefined) {
    return { catalog, wideRows: [...wideRows], fieldOverrides };
  }
  const synthetic = appendSyntheticSbefColumn(
    catalog,
    wideRows,
    keynoteEntry,
    sbefStandard,
  );
  return {
    catalog: synthetic.catalog,
    wideRows: synthetic.wideRows,
    fieldOverrides: { ...fieldOverrides, sbef: synthetic.sbefHeader },
    sbefStandard,
  };
};

/**
 * When the mapped SBEF column is BSAB 96, append a synthetic SBEF column with
 * converted codes (same idea as IFC Quantity/Unit). Source values in `raw` stay
 * as in the file. No-op when the column is already SBEF 83 or empty.
 */
const appendConvertedSbefColumn = (
  catalog: FlatColumnCatalog,
  wideRows: readonly WideRow[],
  columnMapping: ColumnMapping,
  fieldWinner: FieldWinner,
): {
  catalog: FlatColumnCatalog;
  wideRows: WideRow[];
  columnMapping: ColumnMapping;
  fieldWinner: FieldWinner;
  sbefStandard?: BsabStandard;
} => {
  const winnerHeader = fieldWinner.sbef;
  if (!winnerHeader) {
    return { catalog, wideRows: [...wideRows], columnMapping, fieldWinner };
  }
  const sourceEntry = catalog.find(
    (entry) => entry.normalizedHeader === winnerHeader,
  );
  if (!sourceEntry) {
    return { catalog, wideRows: [...wideRows], columnMapping, fieldWinner };
  }
  const sourceValues = wideRows.map((wide) => wide.values[winnerHeader] ?? '');
  const sbefStandard = detectBsabStandard(sourceValues);
  if (sbefStandard !== '96') {
    return {
      catalog,
      wideRows: [...wideRows],
      columnMapping,
      fieldWinner,
      sbefStandard,
    };
  }
  const synthetic = appendSyntheticSbefColumn(
    catalog,
    wideRows,
    sourceEntry,
    sbefStandard,
  );
  const nextMapping = new Map(columnMapping);
  nextMapping.set(winnerHeader, undefined);
  nextMapping.set(synthetic.sbefHeader, 'sbef');
  return {
    catalog: synthetic.catalog,
    wideRows: synthetic.wideRows,
    columnMapping: nextMapping,
    fieldWinner: { ...fieldWinner, sbef: synthetic.sbefHeader },
    sbefStandard,
  };
};

/**
 * When the catalog carries an IFC type column and ≥ 1 measure column, derive a
 * single `Quantity` + `Unit` (+ a hidden `Quantity Source`) per wide row from
 * the IFC type's priority order, and append them as synthetic columns.
 *
 * Returns `fieldOverrides` so the orchestrator can force the winning columns for
 * the IFC/Solibri import path — kept here (not in the global synonym table) so
 * generic headers like `Type` never auto-map unrelated, non-IFC imports:
 * - `name`/`code` map the Solibri `Type` / `Identity Data.Type Mark` columns.
 * - `sbef` maps a native BSAB 83 column when present, else a synthetic `SBEF` column derived from `Identity Data.Keynote`.
 * - `quantity`/`unit` map the synthetic columns; omitted when the file already
 *   has its own quantity + unit (then no synthetic quantity/unit columns are
 *   added — SBEF aliasing still runs).
 *
 * No-op (catalog/rows passed through, no overrides) when the IFC signature is
 * absent.
 */
export const augmentWithIfcQuantities = (
  catalog: FlatColumnCatalog,
  wideRows: readonly WideRow[],
): {
  catalog: FlatColumnCatalog;
  wideRows: WideRow[];
  fieldOverrides?: Partial<Record<NodonField, string>>;
  sbefStandard?: BsabStandard;
} => {
  const ifcEntry =
    catalog.find((e) => headerKey(e.originalHeader) === 'ifc entity') ??
    catalog.find((e) => headerKey(e.originalHeader) === 'ifc type');

  const measureEntries = {} as Record<
    IfcMeasure,
    FlatColumnCatalogEntry | undefined
  >;
  let measureCount = 0;
  for (const measure of Object.keys(IFC_MEASURE_HEADER_KEYS) as IfcMeasure[]) {
    const key = IFC_MEASURE_HEADER_KEYS[measure];
    const entry = catalog.find((e) => headerKey(e.originalHeader) === key);
    measureEntries[measure] = entry;
    if (entry) measureCount++;
  }

  if (!ifcEntry || measureCount === 0) {
    return { catalog: [...catalog], wideRows: [...wideRows] };
  }

  // Solibri field columns, mapped only on this IFC path (see constants).
  const headerFor = (key: string): string | undefined =>
    catalog.find((e) => headerKey(e.originalHeader) === key)?.normalizedHeader;
  const fieldOverrides: Partial<Record<NodonField, string>> = {};
  const nameHeader = headerFor(SOLIBRI_NAME_HEADER_KEY);
  const codeHeader = headerFor(SOLIBRI_CODE_HEADER_KEY);
  if (nameHeader) fieldOverrides.name = nameHeader;
  if (codeHeader) fieldOverrides.code = codeHeader;

  const withSbef = applySolibriSbefColumn(catalog, wideRows, fieldOverrides);

  // If the source already provides its own quantity AND unit columns, respect
  // them — don't synthesize (which would collide with / overwrite the native
  // ones). Only an IFC export lacking both gets derived quantity/unit columns.
  let hasNativeQuantity = false;
  let hasNativeUnit = false;
  for (const entry of withSbef.catalog) {
    const samples = withSbef.wideRows
      .slice(0, SAMPLE_ROW_COUNT)
      .map((w) => w.values[entry.normalizedHeader] ?? '');
    const field = fieldForColumn(entry.originalHeader, samples);
    if (field === 'quantity') hasNativeQuantity = true;
    else if (field === 'unit') hasNativeUnit = true;
  }
  if (hasNativeQuantity && hasNativeUnit) {
    return withSbef;
  }

  const { tag } = ifcEntry;
  const quantityHeader = `${tag}.${IFC_QUANTITY_HEADER}`;
  const unitHeader = `${tag}.${IFC_UNIT_HEADER}`;
  const sourceHeader = `${tag}.${IFC_QUANTITY_SOURCE_HEADER}`;

  const syntheticCatalog: FlatColumnCatalog = [
    [quantityHeader, IFC_QUANTITY_HEADER],
    [unitHeader, IFC_UNIT_HEADER],
    [sourceHeader, IFC_QUANTITY_SOURCE_HEADER],
  ].map(([normalizedHeader, originalHeader]) => ({
    normalizedHeader: normalizedHeader!,
    originalHeader: originalHeader!,
    tag,
    cellIndex: -1,
  }));

  const augmentedRows = withSbef.wideRows.map((wide) => {
    const measures = {} as Record<IfcMeasure, string>;
    for (const measure of Object.keys(
      IFC_MEASURE_HEADER_KEYS,
    ) as IfcMeasure[]) {
      const entry = measureEntries[measure];
      measures[measure] = entry
        ? (wide.values[entry.normalizedHeader] ?? '')
        : '';
    }
    const { quantity, unit, source } = deriveIfcQuantity(
      wide.values[ifcEntry.normalizedHeader] ?? '',
      measures,
    );
    return {
      ...wide,
      values: {
        ...wide.values,
        [quantityHeader]: quantity,
        [unitHeader]: unit,
        [sourceHeader]: source,
      },
    };
  });

  withSbef.fieldOverrides.quantity = quantityHeader;
  withSbef.fieldOverrides.unit = unitHeader;

  return {
    catalog: [...withSbef.catalog, ...syntheticCatalog],
    wideRows: augmentedRows,
    fieldOverrides: withSbef.fieldOverrides,
    sbefStandard: withSbef.sbefStandard,
  };
};

// ---------------------------------------------------------------------------
// Step 3 — buildTagPriority
// ---------------------------------------------------------------------------

/** Step 3 — tag → schema index priority map. */
export const buildTagPriority = (layout: CsvLayout): TagPriority =>
  layout.kind === 'flat'
    ? new Map([[FLAT_TAG, 0]])
    : new Map(layout.schemaRows.map((sr, i) => [sr.tag, i]));

// ---------------------------------------------------------------------------
// Step 4 — mapColumns
// ---------------------------------------------------------------------------

/** Step 4 — map each catalog column to a Nodon field, pick a winner per field. */
export const mapColumns = (
  catalog: FlatColumnCatalog,
  wideRows: readonly WideRow[],
  tagPriority: TagPriority,
): { columnMapping: ColumnMapping; fieldWinner: FieldWinner } => {
  const sample = wideRows.slice(0, SAMPLE_ROW_COUNT);
  const columnMapping = new Map<string, NodonField | undefined>();
  const headersByField = new Map<NodonField, string[]>();

  for (const entry of catalog) {
    const samples = sample.map((w) => w.values[entry.normalizedHeader] ?? '');
    const field = fieldForColumn(entry.originalHeader, samples);
    columnMapping.set(entry.normalizedHeader, field);
    if (field) {
      const list = headersByField.get(field) ?? [];
      list.push(entry.normalizedHeader);
      headersByField.set(field, list);
    }
  }

  const tagOf = new Map(catalog.map((e) => [e.normalizedHeader, e.tag]));
  const priorityOf = (normalizedHeader: string): number =>
    tagPriority.get(tagOf.get(normalizedHeader) ?? '') ?? -1;

  const fieldWinner: Partial<Record<NodonField, string>> = {};
  for (const [field, headers] of headersByField) {
    // Highest tag priority wins; ties broken by normalizedHeader (stable).
    const winner = [...headers].sort((a, b) => {
      const delta = priorityOf(b) - priorityOf(a);
      return delta !== 0 ? delta : a.localeCompare(b);
    })[0];
    if (winner) fieldWinner[field] = winner;
  }

  return { columnMapping, fieldWinner };
};

// ---------------------------------------------------------------------------
// Step 5 — freezePlan
// ---------------------------------------------------------------------------

/**
 * Step 5 — freeze the plan. Gates on required fields: a missing winner for
 * `name`, `code` or `quantity` returns `missing_required_fields` and no body
 * loop runs.
 */
export const freezePlan = (
  layout: CsvLayout,
  catalog: FlatColumnCatalog,
  tagPriority: TagPriority,
  columnMapping: ColumnMapping,
  fieldWinner: FieldWinner,
): FreezeResult => {
  const missing = REQUIRED_FIELDS.filter((field) => !fieldWinner[field]);
  if (missing.length > 0) {
    return {
      ok: false,
      error: { kind: 'missing_required_fields', fields: missing },
    };
  }

  return {
    ok: true,
    plan: {
      layout: layout.kind,
      catalog,
      tagPriority,
      columnMapping,
      fieldWinner,
      leafTag: layout.kind === 'tagged' ? layout.leafTag : undefined,
    },
  };
};

// ---------------------------------------------------------------------------
// Step 6 — buildRows
// ---------------------------------------------------------------------------

/**
 * Per-cell provenance for one wide row, in catalog order. `nodonField` is set
 * only on the column that *won* a field (so a consumer can find the SBEF /
 * unit / quantity cell directly); losing duplicates carry no field.
 */
const cellsForWideRow = (plan: ParsePlan, wide: WideRow): ParsedCell[] => {
  const winnerField = new Map<string, NodonField>(
    (Object.entries(plan.fieldWinner) as [NodonField, string][]).map(
      ([field, header]) => [header, field],
    ),
  );
  return plan.catalog.map((entry) => ({
    normalizedHeader: entry.normalizedHeader,
    originalHeader: entry.originalHeader,
    tag: entry.tag,
    value: wide.values[entry.normalizedHeader] ?? '',
    nodonField: winnerField.get(entry.normalizedHeader),
    // Carried when the column's tag is a parent of this row's leaf tag.
    carried: entry.tag !== wide.tag,
  }));
};

/**
 * Step 6 — one `KalkylfilRow` per wide row; pure value picking. Returns the
 * surviving rows, their per-row cell provenance (parallel to `rows`), and a
 * `row_error` for every dropped row.
 */
export const buildRows = (
  plan: ParsePlan,
  wideRows: readonly WideRow[],
): {
  rows: KalkylfilRow[];
  errors: KalkylfilParseError[];
  cells: ParsedCell[][];
} => {
  const rows: KalkylfilRow[] = [];
  const errors: KalkylfilParseError[] = [];
  const cells: ParsedCell[][] = [];

  const pick = (wide: WideRow, field: NodonField): string => {
    const key = plan.fieldWinner[field];
    return (key ? wide.values[key] : undefined) ?? '';
  };

  wideRows.forEach((wide, index) => {
    const parseMeasure = (
      field: 'quantity' | 'waste',
    ): { value: number | null; error: boolean } => {
      const raw = pick(wide, field);
      if (raw === '') return { value: null, error: false };
      const n = parseNumeric(raw);
      if (Number.isNaN(n)) {
        errors.push({
          kind: 'row_error',
          row: index + 1,
          reason: `Column "${plan.fieldWinner[field]}" has non-numeric value: "${raw}"`,
        });
        return { value: null, error: true };
      }
      return { value: n, error: false };
    };

    const quantity = parseMeasure('quantity');
    const waste = parseMeasure('waste');
    if (quantity.error || waste.error) return; // drop only this row

    const raw: Record<string, string> = {};
    for (const entry of plan.catalog) {
      raw[entry.normalizedHeader] = wide.values[entry.normalizedHeader] ?? '';
    }

    rows.push({
      sbef: pick(wide, 'sbef'),
      code: pick(wide, 'code'),
      name: pick(wide, 'name'),
      quantity: quantity.value,
      unit: pick(wide, 'unit'),
      waste: waste.value,
      raw,
    });
    cells.push(cellsForWideRow(plan, wide));
  });

  return { rows, errors, cells };
};

// ---------------------------------------------------------------------------
// Step 7 — attachCells
// ---------------------------------------------------------------------------

/** Step 7 — per-row cell metadata (provenance) for every wide row. */
export const attachCells = (
  plan: ParsePlan,
  wideRows: readonly WideRow[],
): ParsedCell[][] => wideRows.map((wide) => cellsForWideRow(plan, wide));

// ---------------------------------------------------------------------------
// Public result types
// ---------------------------------------------------------------------------

/**
 * Detected interpretations of ambiguous columns, frozen at parse time. Waste
 * scale and SBEF classification standard live here so more interpretations
 * can be added without another schema change.
 */
export type MetadataInterpretations = {
  /**
   * Scale of the waste / spill column (see {@link WasteScale}). Absent when the
   * CSV has no waste column or every waste cell is empty.
   */
  wasteScale?: WasteScale;
  /**
   * Source classification standard of the SBEF values before conversion
   * (see {@link BsabStandard}). `'96'` means the file was BSAB 96 and a
   * synthetic SBEF 83 column was added. Absent when every cell is empty,
   * `0`, or unrecognised.
   */
  sbefStandard?: BsabStandard;
};

/**
 * Full pipeline metadata, frozen at parse time and persisted for traceability.
 * Maps are stored as entry arrays so the value is plain JSON.
 */
export type ParseMetadata = {
  layout: 'tagged' | 'flat';
  catalog: FlatColumnCatalog;
  tagPriority: [string, number][];
  columnMapping: [string, NodonField | undefined][];
  fieldWinner: Partial<Record<NodonField, string>>;
  leafTag?: string;
  /** Detected column interpretations (DEV-3298). May be absent on legacy imports. */
  interpretations?: MetadataInterpretations;
};

/** Header detection projected from the frozen plan for import consumers. */
export type KalkylfilDetection = {
  /**
   * Live column keys (`Tag.Header` normalized headers) — the keys of
   * {@link KalkylfilRow.raw}. Map each to its original CSV header for display
   * via {@link buildHeaderLabels} over {@link ParseMetadata.catalog}.
   */
  columns: string[];
  /** Nodon field → `Tag.Header` column key. */
  columnMap: KalkylfilColumnMap;
  /** Full pipeline metadata, persisted alongside the import. */
  metadata: ParseMetadata;
};

export type KalkylfilParseResult = {
  rows: KalkylfilRow[];
  /** Per-row cell provenance, parallel to {@link rows}. */
  rowCells: ParsedCell[][];
  errors: KalkylfilParseError[];
  detection: KalkylfilDetection | null;
};

// ---------------------------------------------------------------------------
// Orchestrator — parseKalkylfilCsv
// ---------------------------------------------------------------------------

/** normalizedHeader → originalHeader, for any column in a catalog. */
export const buildHeaderLabels = (
  catalog: FlatColumnCatalog,
): Record<string, string> => {
  const labels: Record<string, string> = {};
  for (const entry of catalog)
    labels[entry.normalizedHeader] = entry.originalHeader;
  return labels;
};

/**
 * Parse a kalkyl CSV into canonical Nodon rows. Runs the seven pipeline steps,
 * then projects to the import-facing contract.
 *
 * `raw` / `detection.columns` / `detection.columnMap` are keyed by the
 * internal `Tag.Header` normalized header — unique even when the source file
 * repeats a column name across schema tags. The original CSV header for each
 * key is in `metadata.catalog` (see {@link buildHeaderLabels}); the UI renders
 * that as the column label. All-empty columns are dropped from the row-facing
 * projection but kept in `metadata.catalog`.
 */
export const parseKalkylfilCsv = (csvText: string): KalkylfilParseResult => {
  const layout = detectLayout(csvText);
  if (!layout) {
    return {
      rows: [],
      rowCells: [],
      errors: [{ kind: 'header_not_found' }],
      detection: null,
    };
  }

  const parsedLines = parseNonEmptyLines(
    normalizeExcelFormulas(csvText).split(/\r?\n/),
  );
  const base = buildCatalogAndWideRows(layout, parsedLines);
  const {
    catalog: augmentedCatalog,
    wideRows: augmentedWideRows,
    fieldOverrides,
    sbefStandard: ifcSbefStandard,
  } = augmentWithIfcQuantities(base.catalog, base.wideRows);
  const tagPriority = buildTagPriority(layout);
  const mapped = mapColumns(augmentedCatalog, augmentedWideRows, tagPriority);

  // Force the IFC/Solibri columns to win their fields. These mappings are scoped
  // to the IFC path (kept out of the global synonym table) so generic headers
  // like `Type` never auto-map unrelated imports; the derived quantity/unit
  // columns also can't be resolved by synonym sampling alone.
  const overrides = Object.entries(fieldOverrides ?? {}) as [
    NodonField,
    string,
  ][];
  const mappedWithOverrides: ColumnMapping = overrides.length
    ? overrides.reduce(
        (map, [field, header]) => map.set(header, field),
        new Map(mapped.columnMapping),
      )
    : mapped.columnMapping;
  const winnerWithOverrides: FieldWinner = overrides.length
    ? overrides.reduce(
        (winner, [field, header]) => ({ ...winner, [field]: header }),
        { ...mapped.fieldWinner } as FieldWinner,
      )
    : mapped.fieldWinner;

  const ensured = appendConvertedSbefColumn(
    augmentedCatalog,
    augmentedWideRows,
    mappedWithOverrides,
    winnerWithOverrides,
  );
  const catalog = ensured.catalog;
  const wideRows = ensured.wideRows;
  const columnMapping = ensured.columnMapping;
  const fieldWinner = ensured.fieldWinner;
  const sbefStandard = ifcSbefStandard ?? ensured.sbefStandard;

  const frozen = freezePlan(
    layout,
    catalog,
    tagPriority,
    columnMapping,
    fieldWinner,
  );

  // A column is "live" if some wide row carries a non-empty value for it.
  // Live columns drive raw / columns; all columns stay in metadata.catalog.
  const liveHeaders = new Set<string>();
  for (const wide of wideRows) {
    for (const [normalized, value] of Object.entries(wide.values)) {
      if (value.trim() !== '') liveHeaders.add(normalized);
    }
  }
  // Keep the winning SBEF column visible even when every converted cell is
  // empty (unmapped BSAB 96 / ignored 0) so the original Keynote still has a
  // sibling SBEF column in the table.
  if (fieldWinner.sbef) liveHeaders.add(fieldWinner.sbef);
  const liveCatalog = catalog.filter((e) =>
    liveHeaders.has(e.normalizedHeader),
  );

  const detection: KalkylfilDetection = {
    columns: liveCatalog.map((e) => e.normalizedHeader),
    columnMap: { ...fieldWinner },
    metadata: {
      layout: layout.kind,
      catalog,
      tagPriority: [...tagPriority],
      columnMapping: [...columnMapping],
      fieldWinner,
      leafTag: layout.kind === 'tagged' ? layout.leafTag : undefined,
      interpretations: {},
    },
  };

  if (!frozen.ok) {
    return { rows: [], rowCells: [], errors: [frozen.error], detection };
  }

  const built = buildRows(frozen.plan, wideRows);
  const rows = built.rows.map((row) =>
    isIgnoredSbefCell(row.sbef) ? { ...row, sbef: '' } : row,
  );
  const { cells, errors } = built;

  // Interpret waste scale and SBEF classification from body values. Frozen
  // here and persisted with the import metadata.
  const wasteScale = detectWasteScale(rows.map((row) => row.waste));
  const interpretations: MetadataInterpretations = {};
  if (wasteScale !== undefined) interpretations.wasteScale = wasteScale;
  if (sbefStandard !== undefined) interpretations.sbefStandard = sbefStandard;
  if (Object.keys(interpretations).length > 0) {
    detection.metadata.interpretations = interpretations;
  }
  const projected = rows.map((row) => {
    const raw: Record<string, string> = {};
    for (const entry of liveCatalog) {
      raw[entry.normalizedHeader] = row.raw[entry.normalizedHeader] ?? '';
    }
    return { ...row, raw };
  });
  return { rows: projected, rowCells: cells, errors, detection };
};
